import {
  apply_review_status,
  build_timesheet_filters,
  build_timesheet_metrics,
  filtered_timesheet_sheets,
  load_timesheet_sheets,
  persist_review_status,
  review_billable_report_sections,
  review_entries,
  timesheet_export_csv,
  timesheet_export_filename,
  timesheet_months,
} from './time_sheets_helpers';
import { read_session_project_id } from '../CompanyCases/company_cases_common';
import BlockingLoaderOverlay from '@/components/config_components/BlockingLoaderOverlay/BlockingLoaderOverlay.vue';

function create_time_sheets_state() {
  return {
    active_filter: 'all',
    selected_month: '',
    selected_week: '',
    selected_review_sheet: null,
    review_comment: '',
    saving_review_action: false,
    months: [],
    sheets: [],
    loading_sheets: false,
    sheets_error: '',
  };
}

function timesheet_request_params() {
  const project_id = read_session_project_id();
  return project_id ? { project_id } : {};
}

function normalize_timesheet_sheets(sheets = []) {
  return Array.isArray(sheets) ? sheets : [];
}

function assign_timesheet_state(component, sheets = []) {
  component.sheets = sheets;
  component.months = timesheet_months(sheets);
  const selected_month_exists = component.months.some((month) => month.value === component.selected_month);
  component.selected_month = selected_month_exists ? component.selected_month : component.months[0]?.value || '';
}

function apply_timesheet_error(component, error) {
  component.sheets_error = component.timesheet_error_message(error);
}

export default {
  name: 'TimeSheets',
  components: {
    BlockingLoaderOverlay,
  },
  data: () => create_time_sheets_state(),
  computed: {
    selected_month_label() {
      return this.months.find((month) => month.value === this.selected_month)?.label || this.selected_month || '';
    },
    month_scoped_sheets() {
      return this.selected_month
        ? this.sheets.filter((sheet) => sheet.month_key === this.selected_month)
        : this.sheets;
    },
    weeks() {
      const week_map = this.month_scoped_sheets.reduce((weeks, sheet) => {
        if (sheet.period && !weeks.has(sheet.period)) {
          weeks.set(sheet.period, sheet.week || `Week of ${sheet.period}`);
        }
        return weeks;
      }, new Map());

      return Array.from(week_map.entries())
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([value, label]) => ({ value, label }));
    },
    period_scoped_sheets() {
      return this.selected_week
        ? this.month_scoped_sheets.filter((sheet) => sheet.period === this.selected_week)
        : this.month_scoped_sheets;
    },
    metrics() {
      return build_timesheet_metrics(this.period_scoped_sheets);
    },
    filters() {
      return build_timesheet_filters(this.period_scoped_sheets);
    },
    filtered_sheets() {
      return filtered_timesheet_sheets(this.period_scoped_sheets, this.active_filter);
    },
    page_loading_message() {
      return 'Loading timesheets...';
    },
    page_loading_detail() {
      return 'Fetching submitted timesheets and billing summaries.';
    },
  },
  methods: {
    set_filter(filter_key) {
      this.active_filter = filter_key;
    },
    review_entries(sheet) {
      return review_entries(sheet);
    },
    review_billable_report_sections(sheet) {
      return review_billable_report_sections(sheet);
    },
    export_timesheets() {
      if (!this.filtered_sheets.length) {
        this.sheets_error = 'There are no timesheets to export for the selected month, week, and status filters.';
        return;
      }

      this.sheets_error = '';
      this.download_text_file(
        timesheet_export_filename(this.selected_month),
        timesheet_export_csv(this.filtered_sheets),
        'text/csv;charset=utf-8;'
      );
    },
    download_text_file(filename, content, type) {
      const blob = new Blob([content], { type });
      const link = document.createElement('a');
      const download_url = URL.createObjectURL(blob);
      link.href = download_url;
      link.setAttribute('download', filename);
      link.setAttribute('rel', 'noopener');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(download_url), 0);
    },
    open_review_modal(sheet) {
      if (!sheet) {
        return;
      }

      this.selected_review_sheet = sheet;
      this.review_comment = sheet.review_comment || '';
      document.body.classList.add('timesheet-review-open');
    },
    close_review_modal() {
      this.selected_review_sheet = null;
      this.review_comment = '';
      this.saving_review_action = false;
      document.body.classList.remove('timesheet-review-open');
    },
    async update_review_status(status) {
      if (!this.selected_review_sheet) {
        return;
      }

      if (status === 'disputed' && !String(this.review_comment || '').trim()) {
        this.sheets_error = 'Add a revision note before returning this timesheet.';
        return;
      }

      this.saving_review_action = true;
      this.sheets_error = '';

      try {
        await persist_review_status(
          this.selected_review_sheet,
          status,
          this.review_comment,
          Number(this.$store?.getters?.currentUserId || 0)
        );
        apply_review_status(this.selected_review_sheet, status);
        this.selected_review_sheet.review_comment = String(this.review_comment || '').trim();
        this.close_review_modal();
        await this.load_time_sheets();
      } catch (error) {
        this.sheets_error = this.timesheet_error_message(error);
      } finally {
        this.saving_review_action = false;
      }
    },
    handle_review_keydown(event) {
      if (event.key === 'Escape' && this.selected_review_sheet) {
        this.close_review_modal();
      }
    },
    async load_time_sheets() {
      this.loading_sheets = true;
      this.sheets_error = '';

      try {
        const sheets = normalize_timesheet_sheets(
          await load_timesheet_sheets(timesheet_request_params())
        );
        assign_timesheet_state(this, sheets);
      } catch (error) {
        apply_timesheet_error(this, error);
      } finally {
        this.loading_sheets = false;
      }
    },
    timesheet_error_message(error) {
      return error?.response?.data?.message || error?.message || 'Failed to load timesheets.';
    },
  },
  watch: {
    selected_month() {
      if (!this.weeks.some((week) => week.value === this.selected_week)) {
        this.selected_week = '';
      }
    },
  },
  mounted() {
    window.addEventListener('keydown', this.handle_review_keydown);
    this.load_time_sheets();
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handle_review_keydown);
    document.body.classList.remove('timesheet-review-open');
  },
};
